import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Which side panels are open, and the notes panel's target patient.
//
// Sissi AI and the patient notes both dock on the right of the page on a
// desktop and are bottom sheets on a phone, so one shows at a time: opening
// one closes the other.

const AGENT_OPEN_KEY = 'clinic-agent-open';
const PHONE_QUERY = '(max-width: 768px)';

export type SidePanel = 'agent' | 'notes' | null;

export type NotesPatient = {
  id: number; // UserProfile id (same id as /clinic/patient/{id}/ routes)
  name: string;
};

export type NotesTab = 'all' | 'pinned';

export interface SidePanelContextValue {
  agentOpen: boolean;
  notesOpen: boolean;
  // The panel in front: the notes when they are open, else Sissi AI, else
  // none. For the surfaces that only ask "is this one showing".
  activePanel: SidePanel;
  notesPatient: NotesPatient | null;
  // Set when a launch surface asks for a specific tab (e.g. the pinned-notes
  // strip opens "Fijadas"). seq bumps on every request so the panel applies it
  // even if the tab value repeats.
  notesTabRequest: { tab: NotesTab; seq: number } | null;
  openNotesForPatient: (patient: NotesPatient, initialTab?: NotesTab) => void;
  closeNotes: () => void;
  openAgent: () => void;
  closeAgent: () => void;
}

const SidePanelContext = createContext<SidePanelContextValue | undefined>(undefined);

const isPhone = () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches;

export const SidePanelProvider = ({ children }: { children: ReactNode }) => {
  const [agentOpen, setAgentOpen] = useState<boolean>(() => {
    // Notes never starts open (its target patient isn't persisted); Sissi AI
    // keeps its historical default: stored preference, else open on desktop.
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(AGENT_OPEN_KEY);
    } catch {
      /* storage blocked: fall back to the default */
    }
    if (stored != null) return stored === 'true';
    return !isPhone();
  });
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesPatient, setNotesPatient] = useState<NotesPatient | null>(null);
  const [notesTabRequest, setNotesTabRequest] = useState<{ tab: NotesTab; seq: number } | null>(
    null,
  );

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_OPEN_KEY, String(agentOpen));
    } catch {
      /* not remembered this time */
    }
  }, [agentOpen]);

  const openNotesForPatient = useCallback((patient: NotesPatient, initialTab?: NotesTab) => {
    setNotesPatient(patient);
    if (initialTab) setNotesTabRequest((prev) => ({ tab: initialTab, seq: (prev?.seq ?? 0) + 1 }));
    setNotesOpen(true);
    setAgentOpen(false);
  }, []);

  const closeNotes = useCallback(() => setNotesOpen(false), []);

  const openAgent = useCallback(() => {
    setAgentOpen(true);
    setNotesOpen(false);
  }, []);

  const closeAgent = useCallback(() => setAgentOpen(false), []);

  const activePanel: SidePanel = notesOpen ? 'notes' : agentOpen ? 'agent' : null;

  const value = useMemo(
    () => ({
      agentOpen,
      notesOpen,
      activePanel,
      notesPatient,
      notesTabRequest,
      openNotesForPatient,
      closeNotes,
      openAgent,
      closeAgent,
    }),
    [agentOpen, notesOpen, activePanel, notesPatient, notesTabRequest, openNotesForPatient, closeNotes, openAgent, closeAgent],
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

export const useSidePanel = (): SidePanelContextValue | null =>
  useContext(SidePanelContext) ?? null;
